/* PRODUCT PAGE: block artwork/player clicks, allow only the custom play button */
(() => {
  document.addEventListener(
    "click",
    (e) => {
      if (!location.pathname.includes("/products/")) return;

      const insideMediaGallery =
        e.target.closest("media-gallery") ||
        e.target.closest(".media-gallery");

      if (!insideMediaGallery) return;

      const isPlayButton = e.target.closest(".gw-audio-toggle");

      // Only the dedicated play button is allowed on product pages
      if (!isPlayButton) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
      }
    },
    true
  );
})();

/**
 * @namespace ThemeEvents
 * @description A collection of theme-specific events that can be used to trigger and listen for changes anywhere in the theme.
 * @example
 * document.dispatchEvent(new VariantUpdateEvent(variant, sectionId, { html }));
 * document.addEventListener(ThemeEvents.variantUpdate, (e) => { console.log(e.detail.variant) });
 */
export class ThemeEvents {
  /** @static @constant {string} Event triggered when a variant is selected */
  static variantSelected = 'variant:selected';
  /** @static @constant {string} Event triggered when a variant is changed */
  static variantUpdate = 'variant:update';
  /** @static @constant {string} Event triggered when the cart items or quantities are updated */
  static cartUpdate = 'cart:update';
  /** @static @constant {string} Event triggered when a cart update fails */
  static cartError = 'cart:error';
  /** @static @constant {string} Event triggered when a media (video, 3d model) is loaded */
  static mediaStartedPlaying = 'media:started-playing';
  // Event triggered when quantity-selector value is changed
  static quantitySelectorUpdate = 'quantity-selector:update';
  /** @static @constant {string} Event triggered when a predictive search is expanded */
  static megaMenuHover = 'megaMenu:hover';
  /** @static @constant {string} Event triggered when a zoom dialog media is selected */
  static zoomMediaSelected = 'zoom-media:selected';
  /** @static @constant {string} Event triggered when a discount is applied */
  static discountUpdate = 'discount:update';
  /** @static @constant {string} Event triggered when changing collection filters */
  static FilterUpdate = 'filter:update';
}

/**
 * Event fired when a variant is selected
 * @extends {Event}
 */
export class VariantSelectedEvent extends Event {
  /**
   * Creates a new VariantSelectedEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The option value id
   */
  constructor(resource) {
    super(ThemeEvents.variantSelected, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * Event fired after a variant is updated
 * @extends {Event}
 */
export class VariantUpdateEvent extends Event {
  /**
   * Creates a new VariantUpdateEvent
   * @param {Object} resource - The new variant object
   * @param {string} resource.id - The id of the variant
   * @param {boolean} resource.available - Whether the variant is available
   * @param {boolean} resource.inventory_management - Whether the variant has inventory management
   * @param {string} [resource.sku] - The SKU of the variant
   * @param {Object} [resource.featured_media] - The featured media of the variant
   * @param {string} [resource.featured_media.id] - The id of the featured media
   * @param {Object} [resource.featured_media.preview_image] - The preview image of the featured media
   * @param {string} [resource.featured_media.preview_image.src] - The src URL of the preview image
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} data - Additional event data
   * @param {Document} data.html - The new document fragment for the variant
   * @param {string} data.productId - The product ID of the updated variant, used to ensure the correct product form is updated
   * @param {Object} [data.newProduct] - If a new product was loaded as part of the variant update (combined listing)
   * @param {string} data.newProduct.id - The id of the new product
   * @param {string} data.newProduct.url - The url of the new product
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.variantUpdate, { bubbles: true });
    this.detail = {
      resource: resource || null,
      sourceId,
      data: {
        html: data.html,
        productId: data.productId,
        newProduct: data.newProduct,
      },
    };
  }
}

/**
 * Event class for cart additions
 * @extends {Event}
 */
export class CartAddEvent extends Event {
  /**
   * Creates a new CartAddEvent
   * @param {Object} [resource] - The new cart object
   * @param {string} [sourceId] - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was added
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(CartAddEvent.eventName, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }

  static eventName = ThemeEvents.cartUpdate;
}

/**
 * Event class for cart updates
 * @extends {Event}
 */
export class CartUpdateEvent extends Event {
  /**
   * Creates a new CartUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {Object} [data] - Additional event data
   * @param {boolean} [data.didError] - Whether the cart operation failed
   * @param {string} [data.source] - The source of the cart update
   * @param {string} [data.productId] - The id of the product card that was updated
   * @param {number} [data.itemCount] - The number of items in the cart
   * @param {string} [data.variantId] - The id of the product variant that was updated
   * @param {Record<string, string>} [data.sections] - The sections affected by the cart operation
   */
  constructor(resource, sourceId, data) {
    super(ThemeEvents.cartUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
      data: {
        ...data,
      },
    };
  }
}

/**
 * Event class for cart errors
 * @extends {Event}
 */
export class CartErrorEvent extends Event {
  /**
   * Creates a new CartErrorEvent
   * @param {string} sourceId - The id of the element the action was triggered from
   * @param {string} message - A message from the server response
   * @param {Object} description - Description from the server response
   * @param {Object} errors - Errors from the server response
   */
  constructor(sourceId, message, description, errors) {
    super(ThemeEvents.cartError, { bubbles: true });
    this.detail = {
      sourceId,
      data: {
        message,
        errors,
        description,
      },
    };
  }
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class QuantitySelectorUpdateEvent extends Event {
  /**
   * Creates a new QuantitySelectorUpdateEvent
   * @param {number} quantity - Quantity value
   * @param {number} [cartLine] - The id of the updated cart line
   */
  constructor(quantity, cartLine) {
    super(ThemeEvents.quantitySelectorUpdate, { bubbles: true });
    this.detail = {
      quantity,
      cartLine,
    };
  }
}

/**
 * Event class for quantity-selector updates
 * @extends {Event}
 */
export class DiscountUpdateEvent extends Event {
  /**
   * Creates a new DiscountUpdateEvent
   * @param {Object} resource - The new cart object
   * @param {string} sourceId - The id of the element the action was triggered from
   */
  constructor(resource, sourceId) {
    super(ThemeEvents.discountUpdate, { bubbles: true });
    this.detail = {
      resource,
      sourceId,
    };
  }
}

/**
 * Event class for media playback starts
 * @extends {Event}
 */
export class MediaStartedPlayingEvent extends Event {
  /**
   * Creates a new MediaStartedPlayingEvent
   * @param {HTMLElement} resource - The element containing the video that emitted the event
   */
  constructor(resource) {
    super(ThemeEvents.mediaStartedPlaying, { bubbles: true });
    this.detail = {
      resource,
    };
  }
}

/**
 * @typedef {Object} SlideshowSelectEventData
 * @property {number} index
 * @property {string | null} id
 * @property {Element} slide
 * @property {number} previousIndex
 * @property {boolean} userInitiated
 * @property {'select' | 'scroll' | 'drag'} trigger
 */

export class SlideshowSelectEvent extends Event {
  /**  @param {SlideshowSelectEventData} data */
  constructor(data) {
    super(SlideshowSelectEvent.eventName, { bubbles: true });
    this.detail = data;
  }

  /** @type {SlideshowSelectEventData} */
  detail;

  static eventName = 'slideshow:select';
}

/**
 * Event class for zoom dialog media selection
 * @extends {Event}
 */
export class ZoomMediaSelectedEvent extends Event {
  /**
   * Creates a new ZoomMediaSelectedEvent
   * @param {number} index - The index of the selected media
   */
  constructor(index) {
    super(ThemeEvents.zoomMediaSelected, { bubbles: true });
    this.detail = {
      index,
    };
  }
}

/**
 * Event class for mega menu hover being hovered over
 * @extends {Event}
 */
export class MegaMenuHoverEvent extends Event {
  constructor() {
    super(ThemeEvents.megaMenuHover, { bubbles: true });
  }
}

/** Event class for facet filtering updates */
export class FilterUpdateEvent extends Event {
  /** @param {URLSearchParams} queryParams */
  constructor(queryParams) {
    super(ThemeEvents.FilterUpdate, { bubbles: true });
    this.detail = {
      queryParams,
    };
  }

  shouldShowClearAll() {
    return [...this.detail.queryParams.entries()].filter(([key]) => key.startsWith('filter.')).length > 0;
  }
}

/* Gleauxwave: collection artwork = PLAY/STOP 20s preview */
(() => {
  const PREVIEW_MS = 20000;
  let previewTimer = null;
  let currentAudio = null;

  const stop = (audio) => {
    if (!audio) return;
    audio.pause();
    audio.currentTime = 0;
  };

  document.addEventListener(
    "click",
    (e) => {
      // Only run on collection pages
      if (!location.pathname.startsWith("/collections/")) return;

      // Find nearest product card
      const card = e.target.closest("product-card, li");
      if (!card) return;

      // Find audio inside that card
      const audio = card.querySelector('audio[data-audio-preview]');
      if (!audio) return;

// 🚫 PRODUCT PAGE: block artwork-triggered previews completely
if (location.pathname.includes('/products/')) {
  const isArtwork = e.target.closest('[data-audio-preview-trigger], a[ref="cardGalleryLink"]');
  const isPlayButton = e.target.closest('.gw-audio-toggle');

  // If it's artwork (not the play button), kill it fully
  if (isArtwork && !isPlayButton) {
    e.stopImmediatePropagation();
    e.stopPropagation();
    return;
  }
}
// Everywhere else on the site, artwork click-to-preview still works.
const clickedLink = e.target.closest('[data-audio-preview-trigger], a[ref="cardGalleryLink"]');
const playButton = e.target.closest('.gw-audio-toggle');

if (!clickedLink && !playButton) return;

e.preventDefault();
e.stopPropagation();
e.stopImmediatePropagation();

      // Stop any other preview currently playing
      if (currentAudio && currentAudio !== audio) {
        stop(currentAudio);
      }
      clearTimeout(previewTimer);

      // Toggle current preview
      if (!audio.paused && audio.currentTime > 0) {
        stop(audio);
        currentAudio = null;
        return;
      }

      audio.currentTime = 0;
      audio.play().catch(() => {});
      currentAudio = audio;

      previewTimer = setTimeout(() => {
        stop(audio);
        if (currentAudio === audio) currentAudio = null;
      }, PREVIEW_MS);
    },
    true
  );
})();

/* Gleauxwave: stop previews on page leave + BFCache restore */
(() => {
  const stopAll = () => {
    document.querySelectorAll("audio[data-audio-preview]").forEach((a) => {
      try {
        a.pause();
        a.currentTime = 0;
      } catch (e) {}
    });
    if (window.__gwPreviewTimer) clearTimeout(window.__gwPreviewTimer);
  };

  // When navigating away (including swipe/forward cache)
  window.addEventListener("pagehide", stopAll);

  // When returning via swipe-back (BFCache restore)
  window.addEventListener("pageshow", (e) => {
    // If restored from BFCache, kill any resumed audio
    if (e.persisted) stopAll();
  });

  // Extra safety: when tab is backgrounded
  document.addEventListener("visibilitychange", () => {
    if (document.hidden) stopAll();
  });
})();

/* Gleauxwave product audio UI hook */
(() => {
  let currentAudio = null;
  let currentUI = null;

  function formatTime(seconds) {
    if (!Number.isFinite(seconds)) return "0:00";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  }

  function updateUI(audio, ui) {
    if (!audio || !ui) return;

    const currentEl = ui.querySelector(".gw-audio-current");
    const totalEl = ui.querySelector(".gw-audio-total");
    const fillEl = ui.querySelector(".gw-audio-progress-fill");

    if (currentEl) currentEl.textContent = formatTime(audio.currentTime || 0);
    if (totalEl) totalEl.textContent = formatTime(audio.duration || 20);

    if (fillEl) {
      const pct = audio.duration ? (audio.currentTime / audio.duration) * 100 : 0;
      fillEl.style.width = `${Math.max(0, Math.min(100, pct))}%`;
    }
  }

  function setPlaying(ui, playing) {
    if (!ui) return;
    ui.classList.toggle("is-playing", playing);
  }

  function resetUI(ui) {
    if (!ui) return;
    setPlaying(ui, false);

    const currentEl = ui.querySelector(".gw-audio-current");
    const fillEl = ui.querySelector(".gw-audio-progress-fill");

    if (currentEl) currentEl.textContent = "0:00";
    if (fillEl) fillEl.style.width = "0%";
  }

  function stopCurrent() {
    if (currentAudio) {
      currentAudio.pause();
      currentAudio.currentTime = 0;
    }
    resetUI(currentUI);
    currentAudio = null;
    currentUI = null;
  }

  function findScope(el) {
    return (
      el.closest(".product") ||
      el.closest(".product-information") ||
      el.closest(".media-gallery") ||
      el.closest("media-gallery") ||
      el.closest("product-card") ||
      el.closest("li") ||
      document
    );
  }

  function findAudio(scope) {
    return scope.querySelector('audio[data-audio-preview], audio');
  }

  function findUI(scope) {
    return scope.querySelector(".gw-audio-ui");
  }

  document.addEventListener(
    "click",
    (e) => {
      const uiButton = e.target.closest(".gw-audio-toggle");

      // ONLY the play button should trigger playback
      if (!uiButton) return;

      const scope = findScope(uiButton);
      const audio = findAudio(scope);
      const ui = findUI(scope);

      if (!audio || !ui) return;

      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();

      if (currentAudio && currentAudio !== audio) {
        stopCurrent();
      }

      if (audio.paused) {
        audio.currentTime = 0;
        audio.play().catch(() => {});
        currentAudio = audio;
        currentUI = ui;
        setPlaying(ui, true);
        updateUI(audio, ui);
      } else {
        stopCurrent();
      }
    },
    true
  );

  document.addEventListener(
    "timeupdate",
    (e) => {
      const audio = e.target;
      if (!(audio instanceof HTMLAudioElement)) return;
      if (audio !== currentAudio || !currentUI) return;
      updateUI(audio, currentUI);
    },
    true
  );

  document.addEventListener(
    "loadedmetadata",
    (e) => {
      const audio = e.target;
      if (!(audio instanceof HTMLAudioElement)) return;

      const scope = findScope(audio);
      const ui = findUI(scope);
      if (!ui) return;

      updateUI(audio, ui);
    },
    true
  );

  document.addEventListener(
    "ended",
    (e) => {
      const audio = e.target;
      if (!(audio instanceof HTMLAudioElement)) return;
      if (audio !== currentAudio) return;
      stopCurrent();
    },
    true
  );

  document.addEventListener(
    "pause",
    (e) => {
      const audio = e.target;
      if (!(audio instanceof HTMLAudioElement)) return;
      if (audio !== currentAudio) return;
      setPlaying(currentUI, false);
    },
    true
  );

  document.addEventListener(
    "play",
    (e) => {
      const audio = e.target;
      if (!(audio instanceof HTMLAudioElement)) return;

      const scope = findScope(audio);
      const ui = findUI(scope);
      if (!ui) return;

      currentAudio = audio;
      currentUI = ui;
      setPlaying(ui, true);
      updateUI(audio, ui);
    },
    true
  );
})();